import { cx } from "../../../../utils/cx";
import type { NavItemDividerType, NavItemType } from "../config";
import { NavItemBase } from "./nav-item";

interface NavListProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** Additional CSS classes to apply to the list. */
    className?: string;
    /** List of items to display. */
    items: (NavItemType | NavItemDividerType)[];
}

/** Exact match for "/", prefix match for sections (so /partners/42 keeps «بازاریاب‌ها» active). */
export const isNavActive = (href: string | undefined, activeUrl: string | undefined) => {
    if (!href || !activeUrl) return false;
    if (href === "/") return activeUrl === "/";
    return activeUrl === href || activeUrl.startsWith(`${href}/`);
};

export const NavList = ({ activeUrl, items, className }: NavListProps) => {
    const activeItem = items.find((item) => isNavActive(item.href, activeUrl) || item.items?.some((subItem) => isNavActive(subItem.href, activeUrl)));
    // Nested sections (/links and /links/stats): only the most specific match is current.
    const currentHref = items
        .filter((item): item is NavItemType => !item.divider && isNavActive(item.href, activeUrl))
        .reduce<string | undefined>((best, item) => (item.href && item.href.length > (best?.length ?? 0) ? item.href : best), undefined);

    return (
        <ul className={cx("flex flex-col px-4 pt-5", className)}>
            {items.map((item, index) => {
                if (item.divider) {
                    return (
                        <li key={index} className="w-full px-0.5 py-2">
                            {item.label ? <span className="block px-2 pt-2 text-xs font-semibold text-quaternary">{item.label}</span> : <hr className="h-px w-full border-none bg-border-secondary" />}
                        </li>
                    );
                }

                if (item.items?.length) {
                    return (
                        <details key={item.label} open={activeItem?.href === item.href} className="appearance-none py-0.25">
                            <NavItemBase badge={item.badge} icon={item.icon} type="collapsible">
                                {item.label}
                            </NavItemBase>

                            <dd>
                                <ul className="pb-1">
                                    {item.items.map((childItem) => (
                                        <li key={childItem.label} className="py-0.25">
                                            <NavItemBase
                                                href={childItem.href}
                                                badge={childItem.badge}
                                                type="collapsible-child"
                                                current={isNavActive(childItem.href, activeUrl)}
                                            >
                                                {childItem.label}
                                            </NavItemBase>
                                        </li>
                                    ))}
                                </ul>
                            </dd>
                        </details>
                    );
                }

                return (
                    <li key={item.label} className="py-px">
                        <NavItemBase type="link" badge={item.badge} icon={item.icon} href={item.href} current={item.href !== undefined && item.href === currentHref}>
                            {item.label}
                        </NavItemBase>
                    </li>
                );
            })}
        </ul>
    );
};
